import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { useToggle } from 'react-use'
import { useUser } from '../useUser'
import { useList } from '../useList'
import { useMutation } from '../useMutation'
import { useError } from '../useError'
import { queries } from './graphql'
import { setSettingMutation } from './setSettingMutation'
import type { AppSetting, SetSettingAppSettingServiceArgs } from '@ztechapps/common.contract'
import { AppSettingScopeNameEnum } from '@ztechapps/common.contract'

export * from './setSettingMutation'

/***
 * Retrieve multiple settings
 * @param appSettingKeys
 * @param filter
 * @param tenantId
 * @param useList
 */
export const useAppSettings = ({
  tenantId,
  appSettingKeys,
  filter,
}: {
  tenantId?: string
  appSettingKeys?: string[]
  filter?: Record<string, any>
}) => {
  const useResult = useUser()
  tenantId = tenantId ?? useResult?.user?.tenantId

  //when the appSettingKeys is available, use it on filtering values
  if (!isEmpty(appSettingKeys)) {
    filter = {
      key: { $in: appSettingKeys },
      'scope.key': tenantId,
    }
  }

  const {
    list: settings,
    loading,
    error,
  } = useList<AppSetting>({
    ...queries.allConfig,
    limit: 0,
    filter,
    options: {
      queryOptions: {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
        skip: !tenantId && isEmpty(appSettingKeys),
      },
    },
  })

  return { settings, loading, error }
}

export type UseAppSettingProps = { tenantId?: string; appSettingKey?: string; filter?: Record<string, any> }
export type UseAppSettingResult = { setting: AppSetting; loading: boolean; error: any }
/***
 * Retrieve and update single setting
 */
export const useAppSetting = ({ tenantId, appSettingKey, filter }: UseAppSettingProps): UseAppSettingResult => {
  const useResult = useUser()
  tenantId = tenantId ?? useResult?.user?.tenantId

  const { settings, loading, error } = useAppSettings({
    tenantId,
    appSettingKeys: appSettingKey ? [appSettingKey] : undefined,
    filter,
  })

  return { setting: get(settings, '[0]'), loading, error }
}

export type UseAppSettingValueResult = { loading: boolean; error: any; value: string }
export type UseAppSettingValueProps = UseAppSettingProps
export const useAppSettingValue = (props: UseAppSettingValueProps): UseAppSettingValueResult => {
  const { setting, loading, error } = useAppSetting(props)

  return {
    value: setting?.value,
    loading,
    error,
  }
}

export type UseUserSettingResult = {
  value: string | undefined
  /** True only until the signed-in user's row first resolves; the refetch after a save never reports it again. */
  loading: boolean
  saving: boolean
  save: (value: string) => Promise<void>
}
/***
 * Read and write the signed-in user's own row of one setting key. The read passes `filter` only: passing
 * `appSettingKey` would replace the filter with the tenant id.
 */
export const useUserSetting = (key: string): UseUserSettingResult => {
  const { user } = useUser()
  const { setting, loading } = useAppSetting({
    filter: { key, 'scope.name': AppSettingScopeNameEnum.user, 'scope.key': user?._id },
  })
  const [execute, , { loading: saving, error }] = useMutation<AppSetting, SetSettingAppSettingServiceArgs>({
    query: setSettingMutation,
    options: { mutationOptions: { refetchQueries: ['allAppSetting'] } },
  })
  useError(error)

  // Latches on the first resolve and never resets, so the refetch after a save cannot report `loading` again.
  const [resolved, setResolved] = useToggle(false)
  if (!resolved && !loading && user?._id) setResolved(true)

  const save = async (value: string) => {
    await execute({ input: { key, value, scopeName: AppSettingScopeNameEnum.user, scopeKey: user._id } })
  }

  return { value: setting?.value, loading: !resolved, saving, save }
}
